import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useBoardStore from 'stores/board-store';

const BRAND = '주식회사 팜인';
const SITE_URL = 'https://www.farm-in.kr';

// 경로별 SEO 정보 — 운영 기준(source of truth)은 백엔드 seo-routes.json 이며, 서버가 index.html 에
// <script id="seo-routes" type="application/json"> 로 같은 맵을 주입한다. 아래 정적 맵은 서버 주입이 없는
// 개발 서버(npm start) 등에서의 대체값이므로 문구를 바꿀 때는 seo-routes.json 을 먼저 고칠 것.
const LOCAL_SEO_MAP = {
  '/': {
    title: '팜인(주식회사 팜인) | 축사용 제습기·축사용 공기청정기·축사 환기 시스템',
    description:
      '주식회사 팜인(팜인)은 축사용 제습기(제습/냉각) AeroQbit-D, 축사용 공기청정기(DEP 악취 저감) AeroQbit-A 등 축사·돈사 환기와 스마트 축사 환경 제어를 전문으로 하는 기업입니다. 견적 문의 042-933-9060.',
  },
  '/company': {
    title: `회사소개 | 축사 환기 시스템 전문기업 | ${BRAND}`,
    description:
      '축사·돈사 환기 시스템 전문기업 주식회사 팜인을 소개합니다. 악취 저감과 습도 조절 기술로 동물복지형 축산 환경을 만들어갑니다.',
  },
  '/product': {
    title: `축사용 제습기 AeroQbit-D · 축사용 공기청정기 AeroQbit-A | 팜인`,
    description:
      '팜인 제품소개. 축사용 제습기 AeroQbit-D(액상 냉매 축사 제습기, 돈사 습도 조절·냉각 기능, 2HP·5HP)와 축사용 공기청정기 AeroQbit-A(DEP 악취 저감, 돈사용 공기청정기·산업용 공기청정기급 500CMH). 축사·돈사 환기와 연계한 악취 저감·습도 조절 솔루션.',
  },
  '/technology': {
    title: `기술소개 | 스마트 축사 환경 제어 ICT | ${BRAND}`,
    description:
      '스마트 축사 ICT 환경 제어 기술을 소개합니다. 온습도·악취 데이터를 기반으로 축사 환기를 자동 제어하여 쾌적한 사육 환경과 동물복지를 실현합니다.',
  },
  '/community': {
    title: `커뮤니티 | ${BRAND}`,
    description:
      '주식회사 팜인 커뮤니티입니다. 축사 환기 시스템 공지사항, 견적문의, FAQ, Q&A, 자료실을 이용하실 수 있습니다.',
  },
  '/community/notice': {
    title: `공지사항 | ${BRAND}`,
    description:
      '주식회사 팜인의 공지사항입니다. 축사·돈사 환기 시스템 관련 새로운 소식과 안내를 확인하세요.',
  },
  '/community/notice/view': {
    title: `공지사항 | ${BRAND}`,
    description:
      '주식회사 팜인 공지사항 상세 내용입니다. 축사 환기 시스템 관련 소식을 확인하세요.',
  },
  '/community/inquiry': {
    title: `견적문의 | ${BRAND}`,
    description:
      '축사·돈사 환기 시스템 견적문의 게시판입니다. 설치 규모와 환경을 알려주시면 맞춤 견적을 안내해 드립니다. 전화 문의 042-933-9060.',
  },
  '/community/inquiry/view': {
    title: `견적문의 | ${BRAND}`,
    description:
      '축사 환기 시스템 견적문의 상세 내용입니다. 돈사 환기 설치 견적과 답변을 확인하세요.',
  },
  '/community/faq': {
    title: `FAQ | ${BRAND}`,
    description:
      '축사 환기 시스템 자주 묻는 질문(FAQ)입니다. 돈사 환기 설치, 악취 저감, 습도 조절, 유지관리에 대한 궁금증을 해결하세요.',
  },
  '/community/qna': {
    title: `Q&A | ${BRAND}`,
    description:
      '주식회사 팜인 Q&A 게시판입니다. 축사·돈사 환기와 스마트 축사 ICT에 대해 자유롭게 질문하고 답변을 받아보세요.',
  },
  '/community/qna/view': {
    title: `Q&A | ${BRAND}`,
    description:
      '주식회사 팜인 Q&A 상세 내용입니다. 축사 환기 시스템 관련 질문과 답변을 확인하세요.',
  },
  '/community/qna/write': {
    title: `Q&A 작성 | ${BRAND}`,
    description:
      '주식회사 팜인 Q&A 작성 페이지입니다. 축사 환기, 악취 저감 등 궁금한 점을 남겨주세요.',
  },
  '/community/qna/modify': {
    title: `Q&A 수정 | ${BRAND}`,
    description:
      '주식회사 팜인 Q&A 수정 페이지입니다. 등록하신 축사 환기 관련 질문을 수정할 수 있습니다.',
  },
  '/community/archive': {
    title: `자료실 | ${BRAND}`,
    description:
      '주식회사 팜인 자료실입니다. 축사 환기 시스템 카탈로그, 제품 사양서 등 돈사 환기 관련 자료를 내려받으세요.',
  },
};

// 서버(SeoPageService)가 주입한 라우트 SEO 맵을 읽어 정적 맵 위에 덮어쓴다 (없으면 정적 맵만 사용)
function readServerSeoMap() {
  try {
    const el = typeof document !== 'undefined' && document.getElementById('seo-routes');
    if (!el || !el.textContent) {
      return null;
    }
    const parsed = JSON.parse(el.textContent);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

const SEO_MAP = { ...LOCAL_SEO_MAP, ...(readServerSeoMap() || {}) };

// 셀렉터로 head 태그를 찾고, 없으면 생성하여 속성을 갱신
function setHeadTag(selector, tagName, attrs, valueAttr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    Object.keys(attrs).forEach((key) => el.setAttribute(key, attrs[key]));
    document.head.appendChild(el);
  }
  el.setAttribute(valueAttr, value);
}

// 라우트 변경 시마다 title·description·canonical 등 SEO 태그를 갱신
// 관리자에서 추가한 일반 게시판 경로(/community/board/:moduleNo[/view])의 SEO 정보 — 게시판명은 DB moduleTitle 사용
function genericBoardSeo(path, getBoard) {
  const matched = path.match(/^\/community\/board\/(\d+)(\/view)?$/);
  if (!matched) {
    return null;
  }
  const board = getBoard(matched[1]);
  if (!board) {
    return null;
  }
  return {
    title: `${board.moduleTitle} | ${BRAND}`,
    description: board.moduleDesc || `주식회사 팜인 ${board.moduleTitle} 게시판입니다.`,
  };
}

export function RouteTitle() {
  const { pathname } = useLocation();
  const boards = useBoardStore((state) => state.boards);
  const getBoard = useBoardStore((state) => state.getBoard);

  useEffect(() => {
    const path = pathname.replace(/\/+$/, '') || '/';
    // 미등록 경로는 홈 문구로 대체하되 canonical/og:url은 현재 경로 유지
    const seo = SEO_MAP[path] || genericBoardSeo(path, getBoard) || SEO_MAP['/'];
    const url = SITE_URL + (path === '/' ? '/' : path);

    document.title = seo.title;
    setHeadTag('meta[name="description"]', 'meta', { name: 'description' }, 'content', seo.description);
    setHeadTag('meta[property="og:title"]', 'meta', { property: 'og:title' }, 'content', seo.title);
    setHeadTag('meta[property="og:description"]', 'meta', { property: 'og:description' }, 'content', seo.description);
    setHeadTag('meta[property="og:url"]', 'meta', { property: 'og:url' }, 'content', url);
    setHeadTag('link[rel="canonical"]', 'link', { rel: 'canonical' }, 'href', url);
  }, [pathname, boards]);

  return null;
}
